import tmplHistorySuggestContainer from "@/../templates/sp/container/historySuggestContainer.html?raw";
import tmplWordSuggestContainer from "@/../templates/sp/container/wordSuggestContainer.html?raw";
import tmplCategorySuggest from "@/../templates/sp/suggest/category.html?raw";
import tmplHistorySuggest from "@/../templates/sp/suggest/history.html?raw";
import tmplItemNumberSuggest from "@/../templates/sp/suggest/itemNumber.html?raw";
import tmplKeywordSuggest from "@/../templates/sp/suggest/keyword.html?raw";
import tmplMakerSuggest from "@/../templates/sp/suggest/maker.html?raw";
import tmplModelNumberSuggest from "@/../templates/sp/suggest/modelNumber.html?raw";
import tmplProductNameSuggest from "@/../templates/sp/suggest/productName.html?raw";
import tmplCampaignTagSuggest from "@/../templates/sp/suggest/tag.html?raw";
import { buildCategorySearchPageUrl, buildDisplayProductName, buildMakerSearchPageUrl, buildProductDetailPageUrl, buildTagCodeSearchPageUrl, createEventHandlerAdapter, findVisibleSuggestRoot, joinCategory, onClickDeleteHistory, splitCategory } from "./common";

/**
 * SP版画面向けの固有処理を行うハンドラーを生成する。
 *
 * @returns {UIHandler}
 */
export function createUIHandlerSP() {
  return {
    renderWordSuggestRoot,
    renderWordSuggests,
    renderHistorySuggestRoot,
    renderHistorySuggests,
    moveActiveSuggest,
    resolveDisplayKeyword,
    selectSuggest,
  };
}

/**
 * ワードサジェストのルート要素を描画する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} target サジェストのルート要素
 * @returns {void}
 */
function renderWordSuggestRoot(context, target) {
  const root = $(tmplWordSuggestContainer);
  target.addClass('search-suggest')
    .empty()
    .append(root);
}

/**
 * ワードサジェストを描画する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} root サジェストのルート要素
 * @param {string} keyword 検索キーワード
 * @param {UniSuggestApiResponse} response ユニサジェストAPIレスポンス
 * @returns {void}
 */
function renderWordSuggests(context, root, keyword, response) {
  const list = root.children('.search-suggest-list');
  list.empty();

  // キーワードサジェスト
  response.response?.keyword?.docs?.item?.forEach(item => {
    const suggest = $(tmplKeywordSuggest);
    suggest.attr('data-keyword', item.word);
    const matchKeywordTag = suggest.find('b');
    const idx = item.word.indexOf(keyword);
    if (idx === -1) {
      matchKeywordTag.parent().text(item.word);
    } else {
      let text = item.word.substring(0, idx);
      matchKeywordTag.before($(document.createTextNode(text)));
      matchKeywordTag.text(keyword);
      text = item.word.substring(idx + keyword.length);
      matchKeywordTag.after($(document.createTextNode(text)));
    }
    list.append(suggest);
  });

  // 品番サジェスト
  response.response?.itemnumber?.docs?.item?.forEach(item => {
    const suggest = $(tmplItemNumberSuggest);
    const displayProductName = buildDisplayProductName(item, false);
    const url = buildProductDetailPageUrl(context.config.searchAppBaseUrl, item.direct_product_no);
    suggest.attr('data-url', url);
    suggest.find('.suggest-key').text(displayProductName);
    list.append(suggest);
  });

  // 型番サジェスト
  response.response?.modelnumber?.docs?.item?.forEach(item => {
    const suggest = $(tmplModelNumberSuggest);
    const displayProductName = buildDisplayProductName(item, false);
    const url = buildProductDetailPageUrl(context.config.searchAppBaseUrl, item.direct_product_no);
    suggest.attr('data-url', url);
    suggest.find('.suggest-key').text(displayProductName);
    list.append(suggest);
  });

  // 商品名サジェスト
  response.response?.productname?.docs?.item?.forEach(item => {
    const suggest = $(tmplProductNameSuggest);
    const displayProductName = buildDisplayProductName(item, true);
    const url = buildProductDetailPageUrl(context.config.searchAppBaseUrl, item.direct_product_no);
    suggest.attr('data-url', url);
    suggest.find('.suggest-key').text(displayProductName);
    list.append(suggest);
  });

  // カテゴリサジェスト
  response.response?.category?.docs?.item?.forEach(item => {
    const suggest = $(tmplCategorySuggest);
    const idList = splitCategory(item.direct_category_id);
    const lastCategoryId = idList[idList.length - 1];
    const nameList = splitCategory(item.direct_category_name);
    const lastCategoryName = nameList.pop();
    if (!lastCategoryName) {
      return;
    }
    const url = buildCategorySearchPageUrl(context.config.searchAppBaseUrl, lastCategoryId);
    suggest.attr('data-url', url);
    suggest.find('.suggest-key').text(lastCategoryName);
    const help = suggest.find('.search-suggest-category-item-help');
    if (nameList.length) {
      const displayHelpText = `[${joinCategory(nameList)}]`;
      help.text(displayHelpText);
    } else {
      help.remove();
    }
    list.append(suggest);
  });

  // メーカーサジェスト
  response.response?.maker?.docs?.item?.forEach(item => {
    const suggest = $(tmplMakerSuggest);
    const url = buildMakerSearchPageUrl(context.config.searchAppBaseUrl, item.direct_maker_id);
    suggest.attr('data-url', url);
    suggest.find('.suggest-key').text(item.direct_maker_name);
    list.append(suggest);
  });

  // キャンペーンハッシュタグサジェスト
  response.response?.campaigntag?.docs?.item?.forEach(item => {
    const suggest = $(tmplCampaignTagSuggest);
    const url = buildTagCodeSearchPageUrl(context.config.searchAppBaseUrl, item.direct_campaign_id);
    suggest.attr('data-url', url);
    suggest.find('.suggest-key').text(item.direct_campaign_tag);
    list.append(suggest);
  });

  list.children('li')
    .on('click', createEventHandlerAdapter(onClickSuggest, context))
    .children('a')
    .on('click', onClickSuggestLink);
}

/**
 * 履歴サジェストのルート要素を描画する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} target 履歴サジェストのルート要素
 * @returns {void}
 */
function renderHistorySuggestRoot(context, target) {
  const root = $(tmplHistorySuggestContainer);
  target.addClass('search-suggest')
    .empty()
    .append(root);

  // 履歴サジェストの削除ボタン
  target.find('#history_suggest_delete')
    .on('click', createEventHandlerAdapter(onClickDeleteHistory, context));
}

/**
 * 履歴サジェストを描画する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} root サジェストのルート要素
 * @param {UniSuggestApiResponse} response ユニサジェストAPIレスポンス
 * @returns {void}
 */
function renderHistorySuggests(context, root, response) {
  const list = root.children('.search-suggest-list');
  list.children('.search-suggest-history-item').remove();
  const clearSuggest = list.children('.search-suggest-history-item-clear');

  response.response?.history?.docs?.item?.forEach(item => {
    const suggest = $(tmplHistorySuggest);
    suggest.children('.suggest-key').text(item.word);
    clearSuggest.before(suggest);
  });

  list.children('.search-suggest-history-item')
    .on('click', createEventHandlerAdapter(onClickSuggest, context));
}

/**
 * サジェストクリック時にサジェストを非表示に変える。
 *
 * @this {HTMLElement}
 * @param {SuggestContext} context サジェストコンテキスト
 * @param {JQuery.ClickEvent} event クリックイベント
 */
function onClickSuggest(context, event) {
  findVisibleSuggestRoot(context.config).hide();

  const $this = $(this);
  const url = $this.attr('data-url') ?? $this.children('a').attr('href');
  if (url) {
    window.location.href = url;
    return;
  }

  const keyword = $this.attr('data-keyword') || $this.find('.suggest-key').text();
  console.assert(!!keyword, 'サジェストキーワードの取得に失敗しました。');
  if (keyword) {
    // 表示されている画面に応じたURLでキーワード検索を行う
    $(context.config.triggerTarget).val(keyword);
    $this.closest('form').trigger('submit');
  }
}

/**
 * サジェスト内のaタグクリック時にリンクを無効化する。
 *
 * @this {HTMLElement}
 * @param {JQuery.ClickEvent} event クリックイベント
 */
function onClickSuggestLink(event) {
  // NOTE: サジェストの種類によってaタグの有無が変わるため、処理共通化のため `onClickSuggest` で一律処理を行う
  event.preventDefault();
}

/**
 * 選択中のサジェスト変更を行う。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} root サジェストのルート要素
 * @param {SuggestMoveDirection} moveDirection 移動方向
 * @returns {JQuery<HTMLElement>} 切り替え後のサジェスト要素
 */
function moveActiveSuggest(context, root, moveDirection) {
  const activeSuggest = root.find('.active');
  let nextActiveSuggest;
  if (!activeSuggest.length) {
    if (moveDirection < 0) {
      nextActiveSuggest = root.find('.search-suggest-list > :last-child');
    } else {
      nextActiveSuggest = root.find('.search-suggest-list > :first-child');
    }
  } else {
    activeSuggest.removeClass('active')
    if (moveDirection < 0) {
      nextActiveSuggest = activeSuggest.prev();
    } else {
      nextActiveSuggest = activeSuggest.next();
    }
  }
  if (nextActiveSuggest.length) {
    nextActiveSuggest.addClass('active');

    // 履歴サジェストのタイトルを選択した場合は、もう一度処理を行う
    const isTitle = nextActiveSuggest.hasClass('search-suggest-history-title');
    if (isTitle) {
      return moveActiveSuggest(context, root, moveDirection);
    }
  }
  return nextActiveSuggest;
}

/**
 * サジェストから画面表示用検索キーワードを取得する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} suggest サジェスト要素
 * @returns {string} 画面表示用検索キーワード
 */
function resolveDisplayKeyword(context, suggest) {
  if (suggest.length && !suggest.hasClass('search-suggest-history-item-clear')) {
    const keyword = suggest.attr('data-keyword') || suggest.find('.suggest-key').text();
    return keyword;
  } else {
    return '';
  }
}

/**
 * サジェスト選択処理を行う。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} suggest サジェスト要素
 */
function selectSuggest(context, suggest) {
  suggest.trigger('click');
}
